/**
 * Contact API Service
 * Handles submitting contact messages to the backend Contact API.
 */

/**
 * Submits contact message to backend POST /api/contact
 * @param {import('axios').AxiosInstance} axiosInstance - Nuxt axios instance ($axios)
 * @param {Object} data - Contact form data
 * @param {string} data.name - Sender name
 * @param {string} data.email - Sender email
 * @param {string} [data.department] - Target department (e.g. Sales, Support, General)
 * @param {string} data.subject - Subject line
 * @param {string} data.message - Message body
 * @param {string} [data.phone] - Phone number to append to message
 * @param {string[]} [data.items] - Associated service items/topics
 * @returns {Promise<{success: boolean, data?: any, status?: number, isRateLimited?: boolean, error?: string}>}
 */
export async function submitContactMessage(
  axiosInstance,
  { name, email, department, subject, message, phone, phoneLabel = 'Phone', items, itemsLabel = 'Services' }
) {
  let messageBody = (message || '').trim();

  // If service items are present, include context
  if (Array.isArray(items) && items.length > 0) {
    messageBody = `[${itemsLabel}: ${items.join(', ')}]\n\n${messageBody}`;
  }

  // Append phone number to message body if provided
  if (phone && phone.trim()) {
    messageBody = `${messageBody}\n\n${phoneLabel}: ${phone.trim()}`;
  }

  const payload = {
    name: (name || '').trim(),
    email: (email || '').trim(),
    department: department || 'General',
    subject: (subject || '').trim(),
    message: messageBody,
  };

  try {
    const response = await axiosInstance.post('/contact', payload);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    const status = error.response ? error.response.status : null;
    const isRateLimited = status === 429;
    const errorMessage =
      error.response && error.response.data && (error.response.data.message || error.response.data.error)
        ? (error.response.data.message || error.response.data.error)
        : error.message;

    return {
      success: false,
      status,
      isRateLimited,
      error: errorMessage,
    };
  }
}
